import React, { useCallback, useEffect, useState } from 'react';
import { Container, Row, Col, Card, Table, Form, InputGroup, Button, Modal, Spinner, Alert, Pagination, Badge } from 'react-bootstrap';
import { FaSearch, FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import PageHeader from '../components/PageHeader';
import ConfirmationModal from '../components/ConfirmationModal';
import categoryService from '../services/categoryService';

function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parent_id: '',
    is_active: true,
    is_featured: false,
    display_order: 0,
    slug: '',
    icon: ''
  });

  const itemsPerPage = 10;

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const data = await categoryService.getAll({ paginate: 'false' });
      setCategories(Array.isArray(data) ? data : data.categories || []);
    } catch (err) {
      setError('Failed to load categories');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleOpenModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        parent_id: category.parent_id || '',
        is_active: category.is_active !== undefined ? category.is_active : true,
        is_featured: category.is_featured !== undefined ? category.is_featured : false,
        display_order: category.display_order !== undefined ? category.display_order : 0,
        slug: category.slug || '',
        icon: category.icon || ''
      });
    } else {
      setEditingCategory(null);
      setFormData({ name: '', description: '', parent_id: '', is_active: true, is_featured: false, display_order: 0, slug: '', icon: '' });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setEditingCategory(null);
    setFormData({ name: '', description: '', parent_id: '', is_active: true, is_featured: false, display_order: 0, slug: '', icon: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Build payload and omit parent_id when it's empty (Top Level)
      const payload = { ...formData };
      if (payload.parent_id === '' || payload.parent_id === null || payload.parent_id === undefined) {
        delete payload.parent_id;
      }

      // Remove slug from payload (handled by backend/auto-generated)
      delete payload.slug;

      if (editingCategory) {
        await categoryService.update(editingCategory.id, payload);
      } else {
        await categoryService.create(payload);
      }
      handleCloseModal();
      fetchCategories();
    } catch (err) {
      setError(`Failed to ${editingCategory ? 'update' : 'create'} category`);
      console.error(err);
    }
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;

    setDeleting(true);
    try {
      await categoryService.delete(categoryToDelete.id);
      setShowDeleteConfirm(false);
      setCategoryToDelete(null);
      fetchCategories();
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error(err);
      setShowDeleteConfirm(false);

      // Extract and display the descriptive error message from the API
      const errorMessage = err.response?.data?.error || err.message || 'Failed to delete category';
      setError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);
  const paginatedCategories = filteredCategories.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="px-4">
      <PageHeader
        title="Categories"
        subtitle={`${filteredCategories.length} categories`}
      />

      <Row className="g-3 mb-4 align-items-center">
        <Col xs={12} md={10}>
          <InputGroup>
            <InputGroup.Text className="bg-white">
              <FaSearch className="text-muted" />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search categories by name or description..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </InputGroup>
        </Col>
        <Col xs={12} md={2} className="d-flex align-items-center">
          <Button
            variant="primary"
            className="w-100 mt-2 mt-md-0"
            onClick={() => handleOpenModal()}
          >
            <FaPlus className="me-2" />
            Add Category
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="bg-danger text-white border-0">
          {error}
        </Alert>
      )}

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="fw-semibold">ID</th>
                  <th className="fw-semibold">Name</th>
                  <th className="fw-semibold">Slug</th>
                  <th className="fw-semibold">Order</th>
                  <th className="fw-semibold">Parent Category</th>
                  <th className="fw-semibold">Description</th>
                  <th className="fw-semibold">Featured</th>
                  <th className="fw-semibold">Status</th>
                  <th className="fw-semibold text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCategories.map((category) => {
                  const parentCategory = categories.find(c => c.id === category.parent_id);
                  return (
                    <tr key={category.id}>
                      <td className="align-middle">{category.id}</td>
                      <td className="align-middle fw-semibold">
                        {category.icon && <i className={`${category.icon} me-2 text-muted`}></i>}
                        {category.name}
                      </td>
                      <td className="align-middle text-muted small">{category.slug}</td>
                      <td className="align-middle">{category.display_order}</td>
                      <td className="align-middle">
                        {parentCategory ? (
                          <span className="badge bg-secondary">{parentCategory.name}</span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="align-middle text-muted">
                        {category.description || 'No description'}
                      </td>
                      <td className="align-middle">
                        {category.is_featured ? (
                          <Badge bg="warning" text="dark">Featured</Badge>
                        ) : (
                          <span className="text-muted small">No</span>
                        )}
                      </td>
                      <td className="align-middle">
                        <Badge bg={category.is_active ? 'success' : 'secondary'}>
                          {category.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="align-middle text-end">
                        <Button
                          variant="link"
                          size="sm"
                          className="text-dark p-1 ms-2"
                          onClick={() => handleOpenModal(category)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="link"
                          size="sm"
                          className="text-dark p-1 ms-2"
                          onClick={() => handleDeleteClick(category)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />

            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                return <Pagination.Ellipsis key={page} disabled />;
              }
              return null;
            })}

            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>
      )}

      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingCategory ? 'Edit Category' : 'Add Category'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    autoFocus
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Icon Class</Form.Label>
                  <Form.Control
                    type="text"
                    name="icon"
                    placeholder="e.g. fa fa-home"
                    value={formData.icon}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            {editingCategory && (
              <Form.Group className="mb-3">
                <Form.Label>Slug</Form.Label>
                <Form.Control
                  type="text"
                  name="slug"
                  value={formData.slug}
                  readOnly
                  disabled
                  className="bg-light"
                />
                <Form.Text className="text-muted">
                  Auto-generated from name (Display Only)
                </Form.Text>
              </Form.Group>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Parent Category (Optional)</Form.Label>
                  <Form.Select
                    name="parent_id"
                    value={formData.parent_id}
                    onChange={handleChange}
                  >
                    <option value="">None (Top Level)</option>
                    {categories
                      .filter(c => !editingCategory || c.id !== editingCategory.id)
                      .map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Order</Form.Label>
                  <Form.Control
                    type="number"
                    name="display_order"
                    value={formData.display_order}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="is_featured"
                    name="is_featured"
                    label="Featured Category"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="is_active"
                    name="is_active"
                    label="Active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ConfirmationModal
        show={showDeleteConfirm}
        onHide={() => {
          setShowDeleteConfirm(false);
          setCategoryToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Category"
        message={`Are you sure you want to delete "${categoryToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={deleting}
      />
    </Container>
  );
}

export default CategoriesPage;
